import React, { lazy } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   variants — the ONE registry of home-page themes.

   Every consumer reads from here so a new theme is a single entry:
   - HomeSwitch.js       → `Component` (lazy — each theme is its own chunk,
                            visitors only download the one that renders)
   - admin HeroSection   → `label` / `desc` / `preview` picker cards
   - Navbar.js           → `forceWhiteNav` (themes that paint their own dark
                            hero band always need white nav text while the
                            navbar is transparent — previously only "trip"
                            was special-cased, which left Horizon's midnight
                            hero with unreadable dark nav text)

   `usesHeroSettings`: whether the theme honours the admin hero options below
   the picker (background images, widget style…). Classic and Aurora render
   them through <Hero>; Trip, Sky, Horizon, Airbnb and Explorer put the saved
   photo behind their own band (useHeroPhoto). Only 3D Clouds paints its own
   sky regardless. The admin hint is built from this flag.

   TO ADD A THEME: create HomeYourTheme.js (+ its own scoped .css — never
   import another theme's stylesheet), compose the shared section components
   and helpers from homeShared.js, then append one entry here. Nothing else
   needs touching.
   ═══════════════════════════════════════════════════════════════════════════ */

export const DEFAULT_HOME_VARIANT = 'classic';

export const HOME_VARIANTS = [
    {
        value: 'classic',
        label: 'Classic',
        desc: 'The original home page — uses all the hero settings below',
        load: () => import('./Home'),
        Component: lazy(() => import('./Home')),
        forceWhiteNav: false,
        usesHeroSettings: true,
        preview: (
            <div className="w-full h-full bg-[#060d1a] relative overflow-hidden rounded-t-xl">
                <div className="absolute top-3 inset-x-8 h-2 rounded-full bg-white/70" />
                <div className="absolute top-7 inset-x-14 h-1.5 rounded-full bg-white/30" />
                <div className="absolute bottom-3 inset-x-6 h-5 rounded-full bg-white/90 shadow" />
                <div className="absolute top-1 left-3 w-1 h-1 rounded-full bg-emerald-400/80" />
                <div className="absolute top-5 right-5 w-1 h-1 rounded-full bg-emerald-400/80" />
                <div className="absolute bottom-10 left-6 w-1 h-1 rounded-full bg-emerald-400/60" />
            </div>
        ),
    },
    {
        value: 'trip',
        label: 'Trip-style',
        desc: 'Modern white redesign — blue banner, floating search card with icon tabs',
        load: () => import('./HomeTrip'),
        Component: lazy(() => import('./HomeTrip')),
        // Paints its own `--hm-primary`-colored gradient hero (.trip-hero__bg)
        // unconditionally — light & dark mode, with or without an uploaded
        // image — so the transparent navbar always needs white text over it.
        forceWhiteNav: true,
        // ...except below 768px, where the band starts UNDER the navbar
        // (HomeTrip.css) so a coloured logo sits on the light page, not on
        // the band — the navbar keeps its normal dark text there.
        phoneLightHeader: true,
        usesHeroSettings: true,
        preview: (
            <div className="w-full h-full bg-slate-100 relative overflow-hidden rounded-t-xl">
                {/* Blue banner */}
                <div className="absolute top-0 inset-x-0 h-[55%] bg-gradient-to-br from-blue-500 to-blue-700" />
                <div className="absolute top-2.5 inset-x-10 h-2 rounded-full bg-white/85" />
                <div className="absolute top-6 inset-x-16 h-1 rounded-full bg-white/45" />
                {/* Floating white search card straddling the boundary */}
                <div className="absolute top-[42%] inset-x-5 h-8 rounded-lg bg-white shadow-md border border-slate-200 p-1">
                    <div className="flex gap-1.5 px-1 pt-0.5">
                        <div className="w-4 h-1 rounded-full bg-blue-500" />
                        <div className="w-4 h-1 rounded-full bg-slate-200" />
                        <div className="w-4 h-1 rounded-full bg-slate-200" />
                    </div>
                    <div className="mt-1 mx-1 h-2.5 rounded-full border border-blue-400" />
                </div>
            </div>
        ),
    },
    {
        value: 'sky',
        label: 'Sky',
        desc: 'Soft gray-to-green gradient with slowly drifting clouds — calm, airy, centered headline + button',
        load: () => import('./HomeSky'),
        Component: lazy(() => import('./HomeSky')),
        // Light gradient hero — dark nav text stays readable, keep the
        // default (auto) behavior.
        forceWhiteNav: false,
        usesHeroSettings: true,
        preview: (
            <div
                className="w-full h-full relative overflow-hidden rounded-t-xl"
                style={{ background: 'linear-gradient(45deg, #F7F7F7 0%, #F7F7F7 70%, #1B8354 200%)' }}
            >
                {/* Simulated soft cloud blobs */}
                <div className="absolute bottom-0 left-2 w-16 h-6 bg-white/70 rounded-full blur-[2px]" />
                <div className="absolute bottom-1 left-10 w-20 h-7 bg-white/50 rounded-full blur-[2px]" />
                <div className="absolute bottom-0 right-3 w-12 h-5 bg-white/60 rounded-full blur-[2px]" />
                {/* Text block */}
                <div className="absolute top-3 inset-x-10 h-1.5 rounded-full bg-slate-500/50" />
                <div className="absolute top-6 inset-x-6 h-2.5 rounded-full bg-emerald-700/80" />
                <div className="absolute top-10 inset-x-9 h-1.5 rounded-full bg-slate-400/40" />
                <div className="absolute top-[52%] left-1/2 -translate-x-1/2 w-10 h-3 rounded-full bg-emerald-700" />
            </div>
        ),
    },
    {
        value: 'aurora',
        label: 'Aurora',
        desc: 'Cinematic photo hero with the Umrah planner as the first search tab — flights, Makkah, Madinah and visa in one widget',
        load: () => import('./HomeAurora'),
        Component: lazy(() => import('./HomeAurora')),
        // Composes the classic <Hero>, so the navbar's existing image-aware
        // logic (hasActiveHeroImages) already picks the right text colour.
        forceWhiteNav: false,
        usesHeroSettings: true,
        preview: (
            <div className="w-full h-full relative overflow-hidden rounded-t-xl bg-[#07100c]">
                {/* Photographic hero suggestion + dark scrim */}
                <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/70 via-[#07100c]/85 to-[#07100c]" />
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-emerald-300/20 blur-[3px]" />
                {/* Headline */}
                <div className="absolute top-3.5 inset-x-9 h-2 rounded-full bg-white/85" />
                <div className="absolute top-7 inset-x-14 h-1 rounded-full bg-white/40" />
                {/* Tab strip — first pill highlighted = Umrah leads */}
                <div className="absolute top-[54%] left-4 flex gap-1">
                    <div className="w-5 h-1.5 rounded-full bg-emerald-400" />
                    <div className="w-4 h-1.5 rounded-full bg-white/35" />
                    <div className="w-4 h-1.5 rounded-full bg-white/35" />
                </div>
                {/* Widget */}
                <div className="absolute bottom-3 inset-x-3 h-5 rounded-lg bg-white shadow-md" />
            </div>
        ),
    },
    {
        value: 'horizon',
        label: 'Horizon',
        desc: 'Premium midnight & gold — night-sky hero with a golden horizon glow, serif headlines, and the floating icon-tab search card',
        load: () => import('./HomeHorizon'),
        Component: lazy(() => import('./HomeHorizon')),
        // Midnight-navy hero painted unconditionally — same situation as
        // "trip": white nav text is always required while transparent.
        forceWhiteNav: true,
        usesHeroSettings: true,
        preview: (
            <div className="w-full h-full relative overflow-hidden rounded-t-xl" style={{ background: 'linear-gradient(165deg,#0b1026 0%,#161a3d 55%,#241f4d 100%)' }}>
                {/* Golden horizon glow */}
                <div className="absolute -bottom-4 inset-x-0 h-10" style={{ background: 'radial-gradient(60% 100% at 50% 100%, rgba(212,169,78,0.65), transparent 75%)' }} />
                {/* Stars */}
                <div className="absolute top-2 left-4 w-0.5 h-0.5 rounded-full bg-white/70" />
                <div className="absolute top-4 right-7 w-0.5 h-0.5 rounded-full bg-white/50" />
                <div className="absolute top-1.5 right-16 w-0.5 h-0.5 rounded-full bg-white/60" />
                {/* Serif headline + gilded underline */}
                <div className="absolute top-3.5 inset-x-9 h-2 rounded-full bg-white/85" />
                <div className="absolute top-7 left-1/2 -translate-x-1/2 w-10 h-0.5 rounded-full" style={{ background: 'linear-gradient(90deg,transparent,#d4a94e,transparent)' }} />
                {/* Floating search card with gold tab */}
                <div className="absolute bottom-3 inset-x-4 h-7 rounded-lg bg-white shadow-md p-1" style={{ border: '1px solid rgba(212,169,78,0.5)' }}>
                    <div className="flex gap-1.5 px-1">
                        <div className="w-4 h-1 rounded-full" style={{ background: '#b8860b' }} />
                        <div className="w-4 h-1 rounded-full bg-slate-200" />
                        <div className="w-4 h-1 rounded-full bg-slate-200" />
                    </div>
                    <div className="mt-1 mx-1 h-2.5 rounded-full" style={{ border: '1px solid #d4a94e' }} />
                </div>
            </div>
        ),
    },
    {
        value: 'bnb',
        label: 'Airbnb-style',
        desc: 'White header with the segmented pill search — Where, dates and guests in one rounded bar; full-screen search flow on mobile',
        load: () => import('./HomeBnb'),
        Component: lazy(() => import('./HomeBnb')),
        // White page + white header band. Not just "auto": the classic
        // hero-image check (hasActiveHeroImages) must NOT apply here — a
        // tenant with hero images saved for Classic would get a white
        // logo/text on this theme's white background (invisible navbar).
        forceWhiteNav: false,
        forceDarkNav: true,
        usesHeroSettings: true,
        preview: (
            <div className="w-full h-full bg-white relative overflow-hidden rounded-t-xl border-b border-slate-100">
                {/* Product tabs */}
                <div className="absolute top-2.5 left-1/2 -translate-x-1/2 flex gap-2">
                    <div className="w-8 h-1.5 rounded-full bg-slate-700" />
                    <div className="w-8 h-1.5 rounded-full bg-slate-200" />
                    <div className="w-8 h-1.5 rounded-full bg-slate-200" />
                </div>
                {/* The segmented pill */}
                <div className="absolute top-7 inset-x-3 h-9 rounded-full bg-white border border-slate-200 shadow-md flex items-center">
                    <div className="flex-1 px-3"><div className="w-8 h-1 rounded-full bg-slate-300" /></div>
                    <div className="w-px h-4 bg-slate-200" />
                    <div className="flex-1 px-3"><div className="w-6 h-1 rounded-full bg-slate-300" /></div>
                    <div className="w-px h-4 bg-slate-200" />
                    <div className="flex-1 px-3"><div className="w-6 h-1 rounded-full bg-slate-300" /></div>
                    <div className="mr-1 w-7 h-7 rounded-full bg-rose-600 flex-shrink-0" />
                </div>
                {/* Content hint */}
                <div className="absolute bottom-2 inset-x-3 flex gap-1.5">
                    <div className="flex-1 h-6 rounded-lg bg-slate-100" />
                    <div className="flex-1 h-6 rounded-lg bg-slate-100" />
                    <div className="flex-1 h-6 rounded-lg bg-slate-100" />
                </div>
            </div>
        ),
    },
    {
        value: 'bnbpro',
        label: 'Airbnb Pro',
        desc: 'The Airbnb-style search, refined — illustrated Hotel / Flight / Tour tabs, a highlight that slides between search fields, calm photo-first listing rows with arrows, and a trust band with airline and payment logos',
        load: () => import('./HomeBnbPro'),
        Component: lazy(() => import('./HomeBnbPro')),
        forceWhiteNav: false,
        forceDarkNav: true,
        usesHeroSettings: true,
        preview: (
            <div className="w-full h-full bg-white relative overflow-hidden rounded-t-xl border-b border-slate-100">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 flex gap-3">
                    <div className="w-4 h-4 rounded bg-amber-200" />
                    <div className="w-4 h-4 rounded bg-sky-200" />
                    <div className="w-4 h-4 rounded-full bg-rose-300" />
                </div>
                <div className="absolute top-8 inset-x-3 h-8 rounded-full bg-slate-100 border border-slate-200 shadow-md flex items-center">
                    <div className="h-full w-[34%] rounded-full bg-white shadow" />
                    <div className="flex-1" />
                    <div className="mr-1 w-6 h-6 rounded-full bg-rose-600 flex-shrink-0" />
                </div>
                <div className="absolute bottom-2 inset-x-3 flex gap-1.5">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="flex-1">
                            <div className="h-6 rounded-md bg-slate-200" />
                            <div className="mt-1 w-3/4 h-1 rounded-full bg-slate-300" />
                        </div>
                    ))}
                </div>
            </div>
        ),
    },
    {
        value: 'sky3d',
        label: '3D Clouds',
        desc: 'Realistic, live 3D clouds rendered in the browser — scrolling flies you down through the clouds, with an airliner crossing overhead',
        load: () => import('./HomeSky3D'),
        Component: lazy(() => import('./HomeSky3D')),
        // Paints its own blue (day) / navy (night) sky behind the navbar.
        forceWhiteNav: true,
        usesHeroSettings: false,
        preview: (
            <div
                className="w-full h-full relative overflow-hidden rounded-t-xl"
                style={{ background: 'linear-gradient(180deg, #3f7fc4 0%, #8cbde6 55%, #eef4fa 100%)' }}
            >
                <div className="absolute bottom-[-6px] left-[-10%] w-[60%] h-10 bg-white/90 rounded-full blur-[5px]" />
                <div className="absolute bottom-[-4px] right-[-10%] w-[65%] h-12 bg-white/85 rounded-full blur-[6px]" />
                <div className="absolute bottom-3 left-[30%] w-[40%] h-7 bg-white/80 rounded-full blur-[4px]" />
                <div className="absolute top-3 inset-x-10 h-2 rounded-full bg-white/90" />
                <div className="absolute top-6 inset-x-16 h-1 rounded-full bg-white/60" />
                <div className="absolute top-[50%] inset-x-6 h-4 rounded-lg bg-white shadow" />
            </div>
        ),
    },
    {
        value: 'explorer',
        label: 'Explorer',
        desc: 'Search-first hero — product tabs over a brand-coloured band, a search card whose fields expand into panels (chips with ×, swap button, two-month calendar, traveller steppers) and quick links below',
        load: () => import('./HomeExplorer'),
        Component: lazy(() => import('./HomeExplorer')),
        // Brand-coloured (or photo) band behind the navbar.
        forceWhiteNav: true,
        usesHeroSettings: true,
        preview: (
            <div className="w-full h-full relative overflow-hidden rounded-t-xl bg-white">
                <div className="absolute inset-x-0 top-0 h-[62%] bg-gradient-to-br from-blue-800 to-blue-500 rounded-b-lg" />
                <div className="absolute top-2 left-1/2 -translate-x-1/2 flex gap-1 p-0.5 rounded-full bg-white/25">
                    <div className="w-5 h-1.5 rounded-full bg-white" />
                    <div className="w-5 h-1.5 rounded-full bg-white/40" />
                    <div className="w-5 h-1.5 rounded-full bg-white/40" />
                </div>
                <div className="absolute top-[34%] inset-x-4 h-10 rounded-lg bg-white shadow-md border border-slate-200 p-1 flex gap-1">
                    <div className="flex-1 rounded border border-blue-400" />
                    <div className="flex-1 rounded border border-slate-200" />
                    <div className="w-5 rounded bg-blue-600" />
                </div>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                    <div className="w-5 h-1.5 rounded-full bg-slate-200" />
                    <div className="w-5 h-1.5 rounded-full bg-slate-200" />
                </div>
            </div>
        ),
    },
];

// Unknown / missing / legacy values resolve to classic so an old saved config
// can never break the homepage.
// Start downloading a theme's chunk before React has rendered HomeSwitch
// (index.js calls this on "/"). Without it the home page loaded in two
// round trips: main.js → HomeSwitch chunk → only THEN the theme's chunks.
// Same import() as the lazy Component, so webpack serves it from cache.
export const preloadHomeVariant = (value) => {
    try { return getHomeVariant(value).load?.(); } catch { return undefined; }
};

export const resolveHomeVariant = (value) =>
    HOME_VARIANTS.some(v => v.value === value) ? value : DEFAULT_HOME_VARIANT;

// ?preview_design=<id> renders another design for THIS page view only —
// nothing is saved and the site's real choice is untouched. The Hero Banner
// picker links here so a locked design can be tried before asking for it.
// Read by HomeSwitch (what renders) and Navbar (so the preview also gets
// that design's nav colours).
export const readPreviewVariant = () => {
    try {
        const v = new URLSearchParams(window.location.search).get('preview_design');
        return v ? resolveHomeVariant(v) : '';
    } catch { return ''; }
};

export const getHomeVariant = (value) =>
    HOME_VARIANTS.find(v => v.value === resolveHomeVariant(value));

// Does this theme always need white nav text while the navbar is transparent
// over its hero? (Themes that paint their own dark band regardless of admin
// hero settings.) Safe on any input — unknown values resolve to classic.
export const variantForcesWhiteNav = (value) => !!getHomeVariant(value).forceWhiteNav;

// The inverse: themes that paint their own LIGHT header band always need dark
// nav text/logo while the navbar is transparent — overriding the hero-image
// and gradient checks that were built for the classic hero.
export const variantForcesDarkNav = (value) => !!getHomeVariant(value).forceDarkNav;

// Below 768px, does the theme keep a light strip behind the navbar (its band
// starting under it)? Then forceWhiteNav must not apply on phones.
export const variantPhoneLightHeader = (value) => !!getHomeVariant(value).phoneLightHeader;
